/** Catalog of `openai/privacy-filter` variants — mirrors `Models.kt`. */

import { isVariantCached } from "./pii.js";
import type { VariantId } from "./settings.js";

export type FileKind = "graph" | "weights" | "tokenizer";

export interface RemoteFile {
  url: string;
  localName: string;
  bytes: number;
  kind: FileKind;
}

export interface Variant {
  id: VariantId;
  displayName: string;
  tagline: string;
  totalBytes: number;
  recommended: boolean;
  files: RemoteFile[];
}

const HF_BASE = "https://huggingface.co/openai/privacy-filter/resolve/main";
const TOKENIZER_BYTES = 27_868_174;
const tokenizerFile: RemoteFile = {
  url: `${HF_BASE}/tokenizer.json`,
  localName: "tokenizer.json",
  bytes: TOKENIZER_BYTES,
  kind: "tokenizer",
};

export const VARIANTS: readonly Variant[] = [
  {
    id: "q4f16",
    displayName: "Compact",
    tagline: "4-bit weights, FP16 activations. Smallest download — best first-load on the web.",
    totalBytes: 809_061_992 + 165_744 + TOKENIZER_BYTES,
    recommended: true,
    files: [
      { url: `${HF_BASE}/onnx/model_q4f16.onnx`, localName: "model_q4f16.onnx", bytes: 165_744, kind: "graph" },
      { url: `${HF_BASE}/onnx/model_q4f16.onnx_data`, localName: "model_q4f16.onnx_data", bytes: 809_061_992, kind: "weights" },
      tokenizerFile,
    ],
  },
  {
    id: "q4",
    displayName: "Small",
    tagline: "4-bit weights, FP32 activations. Good size/quality tradeoff.",
    totalBytes: 917_120_144 + 160_219 + TOKENIZER_BYTES,
    recommended: false,
    files: [
      { url: `${HF_BASE}/onnx/model_q4.onnx`, localName: "model_q4.onnx", bytes: 160_219, kind: "graph" },
      { url: `${HF_BASE}/onnx/model_q4.onnx_data`, localName: "model_q4.onnx_data", bytes: 917_120_144, kind: "weights" },
      tokenizerFile,
    ],
  },
  {
    id: "quantized",
    displayName: "Standard",
    tagline: "INT8 weights. The Android default — heavier but the most balanced.",
    totalBytes: 1_618_042_064 + 162_239 + TOKENIZER_BYTES,
    recommended: false,
    files: [
      { url: `${HF_BASE}/onnx/model_quantized.onnx`, localName: "model_quantized.onnx", bytes: 162_239, kind: "graph" },
      { url: `${HF_BASE}/onnx/model_quantized.onnx_data`, localName: "model_quantized.onnx_data", bytes: 1_618_042_064, kind: "weights" },
      tokenizerFile,
    ],
  },
  {
    id: "fp16",
    displayName: "High accuracy",
    tagline: "FP16 weights. Largest and slowest; the most faithful to the source model.",
    totalBytes: 2_075_624_448 + 741_430_784 + 142_860 + TOKENIZER_BYTES,
    recommended: false,
    files: [
      { url: `${HF_BASE}/onnx/model_fp16.onnx`, localName: "model_fp16.onnx", bytes: 142_860, kind: "graph" },
      { url: `${HF_BASE}/onnx/model_fp16.onnx_data`, localName: "model_fp16.onnx_data", bytes: 2_075_624_448, kind: "weights" },
      { url: `${HF_BASE}/onnx/model_fp16.onnx_data_1`, localName: "model_fp16.onnx_data_1", bytes: 741_430_784, kind: "weights" },
      tokenizerFile,
    ],
  },
] as const;

export function variantById(id: VariantId): Variant | undefined {
  return VARIANTS.find((v) => v.id === id);
}

export function isInstalled(id: VariantId): boolean {
  return isVariantCached(id);
}

export function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
